import { API_BASE_URL } from "src/config/apiBase";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { Repeat, Play, Pause, Pencil, Trash2, ArrowLeft } from "lucide-react";
import "../../CSS/Global.css";
import "../Equipments/ProductFormPage.css";
import { getDecryptedUserFromCookie } from "../utils/getCookie";
import { decryptToken } from "../utils/getToken";

const FREQUENCY_LABEL = {
  weekly: "Weekly",
  monthly: "Monthly",
  quarterly: "Quarterly",
  yearly: "Yearly",
};

const formatDate = (d) => {
  if (!d || d === "0000-00-00") return "—";
  try {
    return new Date(`${d}T12:00:00`).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return d;
  }
};

const RecurringInvoices = () => {
  const navigate = useNavigate();
  const url = API_BASE_URL;
  const token = decryptToken("accessToken") || "";
  const isLoggedIn = getDecryptedUserFromCookie("gta_mainstateuser");
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState(null);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${url}/newinvoice/recurring/all/${isLoggedIn.main_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setTemplates(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error("Failed to load recurring templates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const runAction = async (id, path, successMsg) => {
    setActionId(id);
    try {
      const res = await fetch(`${url}/newinvoice/recurring/${id}/${path}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.reason === "previous_unpaid") {
          toast.warning(
            "Skipped — this customer has an unpaid published invoice. Collect payment first, or edit the template and uncheck “Skip if unpaid”.",
            { autoClose: 9000 }
          );
        } else {
          toast.error(data.message || "Action failed");
        }
        return;
      }
      toast.success(successMsg || data.message);
      if (path === "run-now" && data.invoice?.id) {
        toast.info(`Created ${data.invoice.invoice_number}`, { autoClose: 6000 });
      }
      await fetchTemplates();
    } catch {
      toast.error("Network error");
    } finally {
      setActionId(null);
    }
  };

  const handleDelete = (tpl) => {
    Swal.fire({
      title: "Delete template?",
      text: `"${tpl.template_name}" will be removed. Existing invoices are kept.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      setActionId(tpl.id);
      try {
        const res = await fetch(`${url}/newinvoice/recurring/${tpl.id}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.message);
        toast.success("Template deleted");
        await fetchTemplates();
      } catch (err) {
        toast.error(err.message || "Delete failed");
      } finally {
        setActionId(null);
      }
    });
  };

  const statusBadge = (status) => {
    const s = String(status || "active").toLowerCase();
    const cls =
      s === "active"
        ? "recurring-status recurring-status--active"
        : s === "paused"
          ? "recurring-status recurring-status--paused"
          : "recurring-status recurring-status--completed";
    return <span className={cls}>{s}</span>;
  };

  return (
    <div className="product-form-page">
      <div className="oms-header product-form-page__header">
        <div className="product-form-page__title-wrap">
          <h1 className="dashboard-title">Recurring invoices</h1>
          <p className="product-form-page__subtitle">
            Templates that auto-create invoices on a schedule. Runs daily at 8:00 AM IST.
          </p>
        </div>
        <div className="product-form-page__header-actions">
          <button
            type="button"
            className="product-form-page__back"
            onClick={() => navigate("/invoice")}
          >
            <ArrowLeft size={16} style={{ marginRight: 6 }} />
            Invoices
          </button>
          <button
            type="button"
            className="oms-add-btn"
            onClick={() => navigate("/recurring-invoices/new")}
          >
            New template
          </button>
        </div>
      </div>

      {loading ? (
        <p className="product-form-page__subtitle">Loading…</p>
      ) : templates.length === 0 ? (
        <div className="recurring-empty">
          <Repeat size={40} strokeWidth={1.25} />
          <p>No recurring templates yet.</p>
          <button
            type="button"
            className="oms-add-btn"
            onClick={() => navigate("/recurring-invoices/new")}
          >
            Create your first template
          </button>
        </div>
      ) : (
        <div className="recurring-grid">
          {templates.map((tpl) => (
            <article key={tpl.id} className="recurring-card">
              <div className="recurring-card__head">
                <h3>{tpl.template_name}</h3>
                {statusBadge(tpl.status)}
              </div>
              <p className="recurring-card__customer">{tpl.customer_name}</p>
              <dl className="recurring-card__meta">
                <div>
                  <dt>Frequency</dt>
                  <dd>{FREQUENCY_LABEL[tpl.frequency] || tpl.frequency}</dd>
                </div>
                <div>
                  <dt>Next run</dt>
                  <dd>{formatDate(tpl.next_run_date)}</dd>
                </div>
                <div>
                  <dt>Runs</dt>
                  <dd>{tpl.runs_count || 0}</dd>
                </div>
                <div>
                  <dt>Invoice as</dt>
                  <dd>{tpl.invoice_dpstatus || "Published"}</dd>
                </div>
              </dl>
              <div className="recurring-card__actions">
                <button
                  type="button"
                  title="Run now"
                  disabled={actionId === tpl.id}
                  onClick={() => runAction(tpl.id, "run-now", "Invoice generated")}
                >
                  <Play size={16} />
                </button>
                {tpl.status === "active" ? (
                  <button
                    type="button"
                    title="Pause"
                    disabled={actionId === tpl.id}
                    onClick={() => runAction(tpl.id, "pause", "Template paused")}
                  >
                    <Pause size={16} />
                  </button>
                ) : tpl.status === "paused" ? (
                  <button
                    type="button"
                    title="Resume"
                    disabled={actionId === tpl.id}
                    onClick={() => runAction(tpl.id, "resume", "Template resumed")}
                  >
                    <Repeat size={16} />
                  </button>
                ) : null}
                <button
                  type="button"
                  title="Edit"
                  onClick={() => navigate(`/recurring-invoices/edit/${tpl.id}`)}
                >
                  <Pencil size={16} />
                </button>
                <button
                  type="button"
                  title="Delete"
                  disabled={actionId === tpl.id}
                  onClick={() => handleDelete(tpl)}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </article>
          ))}
        </div>
      )}

      <style>{`
        .recurring-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 1rem;
          padding: 0 1rem 2rem;
        }
        .recurring-card {
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 1rem 1.1rem;
          background: #fff;
        }
        .recurring-card__head {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 0.5rem;
        }
        .recurring-card__head h3 {
          margin: 0;
          font-size: 1rem;
          font-weight: 600;
        }
        .recurring-card__customer {
          margin: 0.35rem 0 0.75rem;
          color: #6b7280;
          font-size: 0.9rem;
        }
        .recurring-card__meta {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0.5rem 1rem;
          margin: 0 0 1rem;
          font-size: 0.85rem;
        }
        .recurring-card__meta dt {
          color: #9ca3af;
          font-weight: 500;
        }
        .recurring-card__meta dd {
          margin: 0;
          font-weight: 600;
        }
        .recurring-card__actions {
          display: flex;
          gap: 0.5rem;
          border-top: 1px solid #f3f4f6;
          padding-top: 0.75rem;
        }
        .recurring-card__actions button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          background: #fafafa;
          cursor: pointer;
        }
        .recurring-card__actions button:hover:not(:disabled) {
          background: #f3f4f6;
        }
        .recurring-card__actions button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .recurring-status {
          font-size: 0.7rem;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          padding: 0.2rem 0.5rem;
          border-radius: 999px;
          font-weight: 600;
        }
        .recurring-status--active { background: #dcfce7; color: #166534; }
        .recurring-status--paused { background: #fef3c7; color: #92400e; }
        .recurring-status--completed { background: #e5e7eb; color: #374151; }
        .recurring-empty {
          text-align: center;
          padding: 3rem 1rem;
          color: #6b7280;
        }
        .recurring-empty p { margin: 1rem 0; }
      `}</style>
    </div>
  );
};

export default RecurringInvoices;
